import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { getStories, getStory } from '../services/api';
import './Stories.css';

interface Story {
  id: number;
  title: string;
  content: string;
  synopsis: string;
  cover_image: string;
}

const Stories: React.FC = () => {
  const [stories, setStories] = useState<Story[]>([]);
  const [loading, setLoading] = useState(true);
  const [readingStory, setReadingStory] = useState<Story | null>(null);

  useEffect(() => {
    getStories()
      .then((res) => setStories(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const openStory = async (id: number) => {
    try {
      const res = await getStory(id);
      setReadingStory(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="stories-page">
      <section className="stories-hero">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            style={{ textAlign: 'center' }}
          >
            <span className="page-badge">Short Fiction</span>
            <h1 className="stories-title">
              Short<br />
              <span className="gradient-text">Stories</span>
            </h1>
            <p className="stories-subtitle">
              Brief but powerful tales that linger long after the last word
            </p>
          </motion.div>
        </div>
      </section>

      <section className="stories-content section">
        <div className="container">
          {loading ? (
            <div className="loading-container">
              <div className="spinner" />
            </div>
          ) : stories.length === 0 ? (
            <div className="empty-state">
              <p>No stories available yet. Check back soon!</p>
            </div>
          ) : (
            <div className="stories-list">
              {stories.map((story, index) => (
                <motion.div
                  key={story.id}
                  className="story-card"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ y: -4 }}
                >
                  <div className="story-card-content">
                    <span className="story-badge">Short Story</span>
                    <h3>{story.title}</h3>
                    <p className="story-synopsis">{story.synopsis}</p>
                    <button
                      className="btn btn-primary"
                      onClick={() => openStory(story.id)}
                    >
                      Read Story
                    </button>
                  </div>
                  <div className="story-card-decoration">
                    <span className="story-infinity">&#8734;</span>
                    <div className="story-lines">
                      <div className="story-line" />
                      <div className="story-line" />
                      <div className="story-line" />
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Story Reader Modal */}
      <AnimatePresence>
        {readingStory && (
          <motion.div
            className="story-reader-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="story-reader"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 50 }}
            >
              <div className="story-reader-toolbar">
                <button
                  className="story-reader-close"
                  onClick={() => setReadingStory(null)}
                >
                  &larr; Back to Stories
                </button>
              </div>
              <article className="story-reader-content">
                <div className="story-reader-header">
                  <span className="story-badge">Short Story</span>
                  <h1>{readingStory.title}</h1>
                  <p className="story-reader-author">by Kutloano Mkhulisi</p>
                  <div className="story-reader-divider">
                    <span>&#8734;</span>
                  </div>
                </div>
                <div className="story-reader-text">
                  {readingStory.content.split('\n\n').map((paragraph, i) => (
                    <p key={i}>{paragraph}</p>
                  ))}
                </div>
                <div className="story-reader-end">
                  <span>&#8734;</span>
                  <p>End of Story</p>
                </div>
              </article>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Stories;
